'use client'

import { cn } from '@/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

/**
 * Wilson Property canonical Tabs component.
 *
 * Figma sources (file 4aIcONIT1Ga1nH3Xn9Xciz — read 2026-07-22):
 *   tab    2198:41383   axes: State × Content
 *   tabs   2198:41426   axis: № of parts (2 / 3 / 4 / 5)
 *
 * The Figma set has **no size axis** — there is exactly one tab size. Do not invent one.
 *
 * ── Track (`TabsList`, 2198:41427) ──────────────────────────────────────────────
 *   bg theme/muted #e0dfe0 · flex items-center · gap/gap-0 0 · padding/p-0 0 ·
 *   radius `sm` 2px · hug width. Children are `shrink-0`.
 *
 * ── Tab (`TabsTrigger`, 2198:41384) ─────────────────────────────────────────────
 *   h 24 · px padding/p-2 8 · py padding/p-1 4 · radius `sm` 2px · overflow clip ·
 *   gap gap/gap-1,5 6px between icon and label · icon 16 (Lucide) ·
 *   label Body/bodySmAlt — Lato `font/weight/regular` 400, font/size/sm 14,
 *   font/lineHeight/sm 16, letterSpacing 0, colour theme/foreground #000, centred.
 *
 *   The label colour is #000 in EVERY state — including active and disabled
 *   (disabled dims the whole tab with opacity, it does not recolour the text).
 *
 * ── States (all six the Figma `State` axis defines) ──────────────────────────────
 *   Inactive       2198:41384 — transparent fill, no shadow
 *   Inactive Hover 2198:41398 — fill theme/secondary #74cdde
 *   Focus          2198:41405 — transparent fill + `focus` effect
 *                               (0 0 0 3px theme/ring #8cc64080)
 *   Active         2198:41412 — fill theme/accent #8cc640 + `shadows/shadow-sm`
 *                               (0 1px 3px rgba(0,0,0,.1), 0 1px 2px -1px rgba(0,0,0,.1))
 *                               — identical to Tailwind v4's `shadow-sm`
 *   Active Focus   2198:41419 — fill theme/accent + the 3px ring, and **no shadow-sm**
 *   Disabled       2198:41391 — transparent fill, opacity 50%
 *
 *   Figma defines NO "active hover" state, so an already-selected tab does not
 *   change on hover. Hover is scoped to `data-[state=inactive]`.
 *   Figma defines no motion for tabs; the short colour/shadow transition here
 *   mirrors the canonical Button's treatment and nothing more.
 *
 * ── Content axis ────────────────────────────────────────────────────────────────
 *   Text / Icon / Icon + Text are purely compositional — pass an `<svg>`, a string,
 *   or both as children. No prop is needed; the base class sizes bare icons to 16px
 *   and the 6px gap only shows when there are two children.
 *
 * ── Not in Figma (deliberately not baked in) ────────────────────────────────────
 *   • UPPERCASE labels. Several *instances* (hero search 4022:82011, featured
 *     listings 4022:82071) set their labels in caps, but the component does not.
 *     Add `className="uppercase"` on the trigger at those call sites.
 *   • Vertical orientation. Provided for keyboard/ARIA completeness only.
 *   • Equal-width tabs. Add `className="flex-1"` on the triggers if a call site
 *     needs it.
 *
 * ── Implementation note ─────────────────────────────────────────────────────────
 * `@radix-ui/react-tabs` is NOT a dependency of this repo and no dependency was
 * added, so this is a self-contained WAI-ARIA tabs implementation: roving tabindex,
 * Arrow/Home/End navigation that skips disabled tabs, optional looping, automatic
 * or manual activation, and role="tablist"/"tab"/"tabpanel" + aria-selected /
 * aria-controls / aria-labelledby wiring. `@radix-ui/react-slot` IS available and is
 * used for `asChild`.
 */

/* -------------------------------------------------------------------------- */
/*                                  variants                                  */
/* -------------------------------------------------------------------------- */

/**
 * Track styling (Figma tabs 2198:41426). Exported so call sites that keep their own
 * state machine can still adopt the Figma skin without adopting the whole component.
 */
// NB: the `shrink-0` Figma puts on each child lives on `tabsTriggerVariants`, not here as
// `[&>*]:shrink-0`. A descendant selector would out-specify a trigger's own `flex-1`, and
// `cn()` can only drop the conflicting class when both sit on the same element.
const tabsListVariants = cva('inline-flex w-fit gap-0 rounded-sm bg-muted p-0', {
  variants: {
    orientation: {
      horizontal: 'flex-row items-center',
      // Not a Figma variant — a11y completeness only.
      vertical: 'flex-col items-stretch',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
})

/**
 * Tab styling (Figma tab 2198:41383), driven entirely off `data-state` /
 * `data-disabled` so it works on a `<button>`, an `<a>` (via `asChild`) or any
 * element a call site drives itself.
 *
 * `data-[state=active]:focus-visible:shadow-none` reproduces Figma's Active Focus
 * (2198:41419), which carries the 3px ring but drops `shadows/shadow-sm`. The extra
 * variant is stacked so it out-specifies `data-[state=active]:shadow-sm` rather than
 * relying on source order.
 */
const tabsTriggerVariants = cva(
  [
    'relative inline-flex h-6 shrink-0 cursor-pointer items-center justify-center gap-1.5',
    'overflow-hidden whitespace-nowrap rounded-sm px-2 py-1',
    'text-body-sm leading-4 font-normal text-foreground',
    'transition-[background-color,box-shadow] duration-200',
    'outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0',
    'data-[state=inactive]:bg-transparent data-[state=inactive]:hover:bg-secondary',
    'data-[state=active]:bg-accent data-[state=active]:shadow-sm',
    'data-[state=active]:focus-visible:shadow-none',
    'disabled:pointer-events-none disabled:opacity-50',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
  ].join(' '),
)

/* -------------------------------------------------------------------------- */
/*                                   context                                  */
/* -------------------------------------------------------------------------- */

type TabsOrientation = 'horizontal' | 'vertical'
type TabsActivationMode = 'automatic' | 'manual'

type TabsContextValue = {
  activationMode: TabsActivationMode
  baseId: string
  hasPanels: boolean
  loop: boolean
  orientation: TabsOrientation
  selectValue: (value: string) => void
  value: string | undefined
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

function useTabsContext(component: string): TabsContextValue {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error(`<${component}> must be rendered inside <Tabs>.`)
  }
  return context
}

/** Values can contain spaces ("For Sale"); ids cannot usefully. */
const partId = (baseId: string, part: 'content' | 'trigger', value: string) =>
  `${baseId}-${part}-${String(value).replace(/[^\w-]+/g, '-')}`

/* -------------------------------------------------------------------------- */
/*                                    Tabs                                    */
/* -------------------------------------------------------------------------- */

export interface TabsProps extends Omit<React.ComponentProps<'div'>, 'defaultValue' | 'onChange'> {
  /**
   * `automatic` (default, WAI-ARIA recommended) selects a tab as soon as arrow-key
   * navigation moves focus onto it. `manual` requires Enter/Space or a click.
   *
   * Use `manual` for link-style tabs (`<TabsTrigger asChild><Link/></TabsTrigger>`)
   * so arrow keys browse without triggering a route change.
   */
  activationMode?: TabsActivationMode
  /** Merge Tabs' behaviour into an existing element instead of adding a wrapper div. */
  asChild?: boolean
  /** Uncontrolled initial selection. */
  defaultValue?: string
  /**
   * Set to `false` for tab rows with no `<TabsContent>` — filter chips that drive a
   * query param, or rows of links. Triggers then omit `aria-controls`, which would
   * otherwise point at an element that does not exist.
   */
  hasPanels?: boolean
  /** Wrap around at the ends during arrow-key navigation. Default `true`. */
  loop?: boolean
  onValueChange?: (value: string) => void
  /** Figma only defines horizontal tabs. `vertical` exists for a11y completeness. */
  orientation?: TabsOrientation
  /** Controlled selection. Pass together with `onValueChange`. */
  value?: string
}

const Tabs: React.FC<TabsProps> = ({
  activationMode = 'automatic',
  asChild = false,
  children,
  className,
  defaultValue,
  hasPanels = true,
  loop = true,
  onValueChange,
  orientation = 'horizontal',
  value: valueProp,
  ...props
}) => {
  const baseId = React.useId()
  const [uncontrolledValue, setUncontrolledValue] = React.useState<string | undefined>(defaultValue)
  const isControlled = valueProp !== undefined
  const value = isControlled ? valueProp : uncontrolledValue

  const selectValue = React.useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolledValue(next)
      onValueChange?.(next)
    },
    [isControlled, onValueChange],
  )

  const context = React.useMemo<TabsContextValue>(
    () => ({ activationMode, baseId, hasPanels, loop, orientation, selectValue, value }),
    [activationMode, baseId, hasPanels, loop, orientation, selectValue, value],
  )

  const Comp = asChild ? Slot : 'div'

  return (
    <TabsContext.Provider value={context}>
      <Comp data-slot="tabs" {...props} className={cn(className)} data-orientation={orientation}>
        {children}
      </Comp>
    </TabsContext.Provider>
  )
}

/* -------------------------------------------------------------------------- */
/*                                  TabsList                                  */
/* -------------------------------------------------------------------------- */

export interface TabsListProps
  extends React.ComponentProps<'div'>, VariantProps<typeof tabsListVariants> {
  /** Render the track as an existing element (e.g. a `<nav>`) instead of a div. */
  asChild?: boolean
}

/** Focusable, non-disabled tabs inside a list, in DOM order. */
function enabledTriggers(list: HTMLElement): HTMLElement[] {
  return Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]')).filter(
    (el) => !el.hasAttribute('disabled') && !el.hasAttribute('data-disabled'),
  )
}

const TabsList: React.FC<TabsListProps> = ({
  asChild = false,
  className,
  onKeyDown,
  orientation: orientationProp,
  ...props
}) => {
  const { loop, orientation: contextOrientation } = useTabsContext('TabsList')
  const orientation = orientationProp ?? contextOrientation

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event)
      if (event.defaultPrevented) return

      const list = event.currentTarget
      const current = (event.target as HTMLElement | null)?.closest<HTMLElement>('[role="tab"]')
      if (!current || !list.contains(current)) return

      const items = enabledTriggers(list)
      const index = items.indexOf(current)
      if (index === -1) return

      const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'
      const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'

      let target: number
      if (event.key === nextKey) target = index + 1
      else if (event.key === prevKey) target = index - 1
      else if (event.key === 'Home') target = 0
      else if (event.key === 'End') target = items.length - 1
      else return

      if (target < 0) target = loop ? items.length - 1 : 0
      if (target >= items.length) target = loop ? 0 : items.length - 1

      event.preventDefault()
      // Focus alone activates the tab when activationMode is `automatic` — see the
      // onFocus handler on TabsTrigger.
      items[target]?.focus()
    },
    [loop, onKeyDown, orientation],
  )

  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-slot="tabs-list"
      role="tablist"
      {...props}
      aria-orientation={orientation}
      className={cn(tabsListVariants({ orientation }), className)}
      data-orientation={orientation}
      onKeyDown={handleKeyDown}
    />
  )
}

/* -------------------------------------------------------------------------- */
/*                                 TabsTrigger                                */
/* -------------------------------------------------------------------------- */

export interface TabsTriggerProps extends React.ComponentProps<'button'> {
  /**
   * Render the tab as the child element — used for link-style tabs:
   * `<TabsTrigger asChild value="buy"><Link href="/buy">Buy</Link></TabsTrigger>`.
   * `disabled` is not a valid attribute on an `<a>`, so `aria-disabled` +
   * `data-disabled` + `pointer-events-none` carry the semantics instead.
   */
  asChild?: boolean
  /** Identifies this tab and its matching `<TabsContent value>`. Required. */
  value: string
}

const TabsTrigger: React.FC<TabsTriggerProps> = ({
  asChild = false,
  className,
  disabled = false,
  onClick,
  onFocus,
  value,
  ...props
}) => {
  const {
    activationMode,
    baseId,
    hasPanels,
    orientation,
    selectValue,
    value: selectedValue,
  } = useTabsContext('TabsTrigger')

  const isSelected = selectedValue === value

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (disabled || event.defaultPrevented) return
    selectValue(value)
  }

  const handleFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
    onFocus?.(event)
    if (disabled || activationMode !== 'automatic' || isSelected) return
    selectValue(value)
  }

  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="tabs-trigger"
      role="tab"
      {...(asChild ? {} : { type: 'button' as const })}
      {...props}
      aria-controls={hasPanels ? partId(baseId, 'content', value) : undefined}
      aria-disabled={disabled || undefined}
      aria-selected={isSelected}
      className={cn(tabsTriggerVariants(), asChild && disabled && 'pointer-events-none', className)}
      data-disabled={disabled || undefined}
      data-orientation={orientation}
      data-state={isSelected ? 'active' : 'inactive'}
      data-value={value}
      id={partId(baseId, 'trigger', value)}
      onClick={handleClick}
      onFocus={handleFocus}
      // Roving tabindex. When nothing is selected yet (no `value`/`defaultValue`)
      // every tab stays tabbable so the list is never keyboard-unreachable; it
      // becomes a proper roving set as soon as a value exists.
      tabIndex={isSelected || selectedValue === undefined ? 0 : -1}
      {...(asChild ? {} : { disabled })}
    />
  )
}

/* -------------------------------------------------------------------------- */
/*                                 TabsContent                                */
/* -------------------------------------------------------------------------- */

export interface TabsContentProps extends React.ComponentProps<'div'> {
  asChild?: boolean
  /**
   * Keep the panel mounted while inactive (hidden via the `hidden` attribute).
   * Use it for panels that must not lose state or re-fetch — maps, iframes,
   * carousels, anything measuring its own DOM.
   */
  forceMount?: boolean
  /** Must match the `value` of its `<TabsTrigger>`. */
  value: string
}

const TabsContent: React.FC<TabsContentProps> = ({
  asChild = false,
  className,
  forceMount = false,
  value,
  ...props
}) => {
  const { baseId, orientation, value: selectedValue } = useTabsContext('TabsContent')
  const isSelected = selectedValue === value

  if (!isSelected && !forceMount) return null

  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-slot="tabs-content"
      role="tabpanel"
      {...props}
      aria-labelledby={partId(baseId, 'trigger', value)}
      className={cn('outline-none', className)}
      data-orientation={orientation}
      data-state={isSelected ? 'active' : 'inactive'}
      hidden={!isSelected}
      id={partId(baseId, 'content', value)}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsListVariants, tabsTriggerVariants }
